// Story 6.17 — platform period close data hooks.
//
// The close waterfall (GET), the VAT remittance that drains vat_payable (POST),
// and the physical wallet sweep (POST). Auth is the admin session cookie.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type VatByCountry = {
  country: string;
  country_source: string;
  vat_cents: number;
};

export type PlatformWalletDrift = {
  wallet_kind: string;
  status: string;
  drift_cents: number;
  detail: string | null;
};

export type PeriodComparison = {
  period_from: string;
  period_to: string;
  output_vat_accrued_cents: number;
  platform_revenue_accrued_cents: number;
};

export type PeriodFiling = {
  id: string;
  country: string;
  output_vat_accrued_cents: number;
  input_vat_offset_cents: number;
  remitted_cents: number;
  payment_reference: string | null;
  note: string | null;
  filed_at: string;
};

// ---------------------------------------------------------------------------
// Story 11.8 — the bank leg, the derived backing, the coverage number and the
// spendable residual.
// ---------------------------------------------------------------------------

/// ⚑ The bank leg's own trustworthiness. `status` is "verified" | "stale" |
/// "missing" and is NEVER rendered apart from the number it qualifies.
export type BankLegView = {
  source: string;
  status: string;
  verified: boolean;
  balance_cents: number | null;
  currency: string | null;
  as_of: string | null;
  recorded_at: string | null;
  recorded_by: string | null;
  reference: string | null;
  note: string | null;
  age_secs: number | null;
  staleness_threshold_secs: number;
  read_error: string | null;
};

/// Face credit outstanding vs the cash backing it (H1). Every figure except the
/// acquiring pair is derived from the stamped top-up rows; the acquiring
/// component is a NAMED, DISPLAYED assumption, because no PSP fee leg exists in
/// the ledger.
export type FaceBacking = {
  face_credit_spendable_cents: number;
  face_credit_escrow_cents: number;
  face_credit_outstanding_cents: number;
  stamped_gross_cents: number;
  stamped_output_vat_cents: number;
  cash_retained_cents: number;
  acquiring_fee_bps: number;
  acquiring_cost_cents: number;
  net_cash_pool_cents: number;
  backing_per_face_euro_bps: number;
  taxed_gross_cents: number;
  untaxed_gross_cents: number;
  mixed_pool: boolean;
  ledger_cash_cents: number;
};

export type CoverageReference = {
  label: string;
  vat_rate_bps: number;
  gross_cents: number;
  vat_cents: number;
  acquiring_cents: number;
  net_cash_pool_cents: number;
  worst_case_obligation_cents: number;
  coverage_bps: number;
};

export type SolvencyCoverage = {
  g_bps: number;
  acquiring_fee_bps: number;
  dk_rate_bps: number | null;
  worst_case_obligation_cents: number;
  coverage_bps: number | null;
  breached: boolean;
  reference_toggle_off: CoverageReference;
  reference_dk_registered: CoverageReference | null;
};

export type SpendableResidual = {
  basis: string;
  verified: boolean;
  cash_cents: number;
  creator_payable_cents: number;
  creator_earnings_cents: number;
  creator_liability_cents: number;
  vat_reserve_cents: number;
  unresearched_provision_cents: number;
  spendable_cents: number;
  floor_breached: boolean;
  floor_shortfall_cents: number;
  bank: BankLegView;
};

export type CloseWaterfall = {
  period_from: string;
  period_to: string;
  wallet_total_cents: number;
  revenue_wallet_cents: number;
  vat_wallet_cents: number;
  creator_liability_wallet_cents: number;
  reserve_wallet_cents: number;
  unswept_cents: number;
  available_cents: number;
  unexplained_cents: number;
  reserve_modelled: boolean;
  wallets_from_ledger: boolean;
  ledger_vat_payable_cents: number;
  ledger_creator_liability_cents: number;
  ledger_platform_revenue_cents: number;
  vat_by_country: VatByCountry[];
  output_vat_accrued_cents: number;
  platform_revenue_accrued_cents: number;
  reconciliation_drift: PlatformWalletDrift[];
  previous_period: PeriodComparison | null;
  filings: PeriodFiling[];
  // CURRENT snapshot (not period-scoped): what all users hold on their accounts.
  user_holdings_total_cents: number;
  user_holdings_escrow_cents: number;
  // Story 11.8 — all three are CURRENT, not period-scoped: "can I spend this"
  // and "is the face credit covered" are questions about right now.
  backing: FaceBacking;
  solvency: SolvencyCoverage;
  spendable: SpendableResidual;
};

async function fetchClose(from: string, to: string): Promise<CloseWaterfall> {
  const qs = new URLSearchParams({ from, to });
  const response = await fetch(`/api/v1/admin/finance/close?${qs.toString()}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Close fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  const body = (await response.json()) as { data: CloseWaterfall };
  return body.data;
}

export function usePlatformClose(from: string, to: string, enabled: boolean) {
  return useQuery<CloseWaterfall, Error>({
    queryKey: ["admin-platform-close", from, to],
    queryFn: () => fetchClose(from, to),
    enabled,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
}

export type RemittanceInput = {
  period_from: string;
  period_to: string;
  country: string;
  remitted_cents: number;
  input_vat_offset_cents: number;
  payment_reference?: string;
  note?: string;
};

async function postRemittance(input: RemittanceInput) {
  const response = await fetch("/api/v1/admin/finance/vat-remittance", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": crypto.randomUUID(),
    },
    body: JSON.stringify(input),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Remittance failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  return response.json();
}

export function useRecordRemittance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: postRemittance,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-platform-close"] }),
  });
}

async function postSweep() {
  const response = await fetch("/api/v1/admin/finance/sweep", {
    method: "POST",
    credentials: "include",
    headers: { "x-idempotency-key": crypto.randomUUID() },
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`Sweep failed (HTTP ${response.status})`);
  }
  return response.json();
}

export function useRunSweep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: postSweep,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-platform-close"] }),
  });
}

// ---------------------------------------------------------------------------
// Story 11.8 — the VAT filing report (momsangivelse box shape)
// ---------------------------------------------------------------------------

export type OutputVatRow = {
  /// ⚑ The state the money is OWED to — what decides which return it goes on.
  owed_state: string;
  /// Where the BUYER was. Under rule (c) these differ, and both are shown.
  buyer_state: string;
  rail: string;
  rail_label: string;
  regime: string;
  registration_state: string;
  topup_count: number;
  gross_cents: number;
  stamped_vat_cents: number;
  ledger_vat_cents: number;
  stamp_ledger_mismatch: boolean;
  momsangivelse_field: string;
};

export type RefundReversalRow = {
  owed_state: string;
  reversal_count: number;
  reversed_vat_cents: number;
  momsangivelse_field: string;
};

export type CreatorVatRow = {
  line: string;
  label: string;
  establishment: string;
  settlement_count: number;
  amount_cents: number;
  momsangivelse_field: string;
  note: string | null;
};

export type UnresearchedFilingRow = {
  country: string;
  topup_count: number;
  gross_cents: number;
  charged_vat_cents: number;
  provision_held_cents: number;
};

export type FilingReport = {
  period_from: string;
  period_to: string;
  provisional_basis: string;
  field_mapping_status: string;
  output_vat: OutputVatRow[];
  refund_reversals: RefundReversalRow[];
  creator_vat: CreatorVatRow[];
  unresearched: UnresearchedFilingRow[];
  salgsmoms_cents: number;
  koebsmoms_cents: number;
  deductible_input_note: string;
  reverse_charge_output_cents: number;
  reverse_charge_input_cents: number;
  rubrik_a_ydelser_cents: number;
  net_vat_payable_cents: number;
  net_vat_payable_without_deduction_cents: number;
  total_gross_cents: number;
  total_topups: number;
  unresearched_gross_cents: number;
  unresearched_provision_held_cents: number;
  unlocatable_gross_cents: number;
  unlocatable_topup_count: number;
  any_stamp_ledger_mismatch: boolean;
};

async function fetchFilingReport(from: string, to: string): Promise<FilingReport> {
  const qs = new URLSearchParams({ from, to });
  const response = await fetch(`/api/v1/admin/finance/vat-report?${qs.toString()}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `VAT report fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  const body = (await response.json()) as { data: FilingReport };
  return body.data;
}

export function useVatFilingReport(from: string, to: string, enabled: boolean) {
  return useQuery<FilingReport, Error>({
    queryKey: ["admin-vat-filing-report", from, to],
    queryFn: () => fetchFilingReport(from, to),
    enabled,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
}

// ---------------------------------------------------------------------------
// Story 11.8 — recording an observed bank balance
//
// ⛔ THIS MOVES NO MONEY. It records what the operator read off the bank, and
// the date they read it for. There is no payment path behind it.
// ---------------------------------------------------------------------------

export type BankBalanceInput = {
  source: "manual" | "statement_import";
  balance_cents: number;
  currency: string;
  /// RFC 3339. ⚑ REQUIRED — a balance with a manufactured date is the failure
  /// this whole surface exists to prevent.
  as_of: string;
  reference?: string;
  note?: string;
};

async function postBankBalance(input: BankBalanceInput) {
  const response = await fetch("/api/v1/admin/finance/bank-balance", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": crypto.randomUUID(),
    },
    body: JSON.stringify(input),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Recording the bank balance failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  return response.json();
}

export function useRecordBankBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: postBankBalance,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-platform-close"] }),
  });
}

/// A coverage figure in basis points, as "2.46x". 10_000 bps = 1.00x.
export function formatCoverage(bps: number | null): string {
  if (bps === null) return "—";
  return `${(bps / 10_000).toFixed(2)}x`;
}

/// Basis points as a percentage, e.g. 2500 -> "25.0%".
export function formatBpsPercent(bps: number): string {
  return `${(bps / 100).toFixed(1)}%`;
}

/// Cash backing per face euro, e.g. 8850 -> "€0.885".
export function formatBackingPerEuro(bps: number): string {
  return `€${(bps / 10_000).toFixed(3)}`;
}

/// A duration in seconds as a human age, for the bank leg's "as of".
export function formatAge(secs: number | null): string {
  if (secs === null) return "—";
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3_600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86_400) return `${Math.floor(secs / 3_600)}h ago`;
  return `${Math.floor(secs / 86_400)}d ago`;
}

export function formatEur(cents: number): string {
  const neg = cents < 0;
  const abs = Math.abs(cents);
  return `${neg ? "-" : ""}€${(abs / 100).toLocaleString("en-IE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
